import * as RPopover from '@radix-ui/react-popover';
import { Check } from 'lucide-react';
import {
  Children,
  Fragment,
  forwardRef,
  isValidElement,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { cn } from '../cn';

interface Opt {
  value: string;
  label: string;
  disabled: boolean;
  group?: string;
}

function textOf(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (isValidElement<{ children?: ReactNode }>(node)) return textOf(node.props.children);
  return '';
}

/** Lee los <option> (y <optgroup>) de los hijos, como los lee el navegador. */
function readOptions(children: ReactNode, group?: string, out: Opt[] = []): Opt[] {
  Children.forEach(children, (c) => {
    if (!isValidElement<{ children?: ReactNode; value?: unknown; label?: string; disabled?: boolean }>(c)) return;
    if (c.type === Fragment) readOptions(c.props.children, group, out);
    else if (c.type === 'optgroup') readOptions(c.props.children, c.props.label, out);
    else if (c.type === 'option') {
      const label = c.props.label ?? textOf(c.props.children);
      out.push({ value: c.props.value === undefined ? label : String(c.props.value), label, disabled: !!c.props.disabled, group });
    }
  });
  return out;
}

const setNativeValue = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value')?.set;

/**
 * Lista desplegable con el mismo uso que un <select> (value, onChange con e.target.value, <option>, register de
 * react-hook-form), pero se abre como los menús de la plataforma: redondeada, con sombra y el elegido marcado.
 * Por dentro queda un <select> oculto que guarda el valor, recibe el ref y dispara el onChange de siempre.
 */
export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select(
  {
    className,
    children,
    style,
    id,
    title,
    disabled,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby,
    'aria-describedby': ariaDescribedby,
    'aria-invalid': ariaInvalid,
    ...rest
  },
  ref,
) {
  const native = useRef<HTMLSelectElement | null>(null);
  const list = useRef<HTMLDivElement | null>(null);
  const options = readOptions(children);
  const controlled = rest.value !== undefined;
  const [uncontrolled, setUncontrolled] = useState<string>(() =>
    rest.defaultValue !== undefined ? String(rest.defaultValue) : (options.find((o) => !o.disabled)?.value ?? ''),
  );
  // Sin "value", el valor lo puede cambiar otro (por ejemplo, reset() de react-hook-form): se relee del <select>.
  // Corre en cada render a propósito; solo actualiza si cambió, así que no entra en un ciclo.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (!controlled && native.current && native.current.value !== uncontrolled) setUncontrolled(native.current.value);
  });
  const current = controlled ? String(rest.value) : uncontrolled;
  const selected = options.find((o) => o.value === current) ?? options[0];

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);

  const setRef = useCallback(
    (el: HTMLSelectElement | null) => {
      native.current = el;
      if (typeof ref === 'function') ref(el);
      else if (ref) ref.current = el;
    },
    [ref],
  );

  const choose = (o: Opt) => {
    if (o.disabled) return;
    setOpen(false);
    const el = native.current;
    if (!el || o.value === current) return;
    setNativeValue?.call(el, o.value);
    el.dispatchEvent(new Event('change', { bubbles: true }));
    if (!controlled) setUncontrolled(o.value);
  };

  const move = (from: number, step: number) => {
    for (let i = 1; i <= options.length; i++) {
      const n = (from + step * i + options.length * 2) % options.length;
      if (!options[n]!.disabled) return n;
    }
    return from;
  };
  const show = (i: number) => {
    setActive(i);
    list.current?.querySelector(`[data-index="${i}"]`)?.scrollIntoView({ block: 'nearest' });
  };
  const typed = useRef({ text: '', at: 0 });
  const onListKey = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      show(move(active, e.key === 'ArrowDown' ? 1 : -1));
    } else if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      show(e.key === 'Home' ? move(-1, 1) : move(options.length, -1));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (options[active]) choose(options[active]);
    } else if (e.key === 'Tab') {
      setOpen(false);
    } else if (e.key.length === 1) {
      // Escribir las primeras letras salta a esa opción (como en un <select>).
      const now = Date.now();
      typed.current = { text: (now - typed.current.at < 700 ? typed.current.text : '') + e.key.toLowerCase(), at: now };
      const i = options.findIndex((o) => !o.disabled && o.label.toLowerCase().startsWith(typed.current.text));
      if (i >= 0) show(i);
    }
  };

  return (
    <>
      <select
        ref={setRef}
        {...rest}
        disabled={disabled}
        className="bf-select-native"
        tabIndex={-1}
        aria-hidden
      >
        {children}
      </select>
      <RPopover.Root open={open} onOpenChange={setOpen} modal={false}>
        <RPopover.Trigger asChild>
          <button
            type="button"
            id={id}
            title={title}
            disabled={disabled}
            className={cn('bf-select', className)}
            style={style}
            role="combobox"
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledby}
            aria-describedby={ariaDescribedby}
            aria-invalid={ariaInvalid}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                setOpen(true);
              }
            }}
          >
            <span className="bf-select-value">{selected?.label ?? ''}</span>
          </button>
        </RPopover.Trigger>
        <RPopover.Portal>
          <RPopover.Content
            className="bf-menu bf-select-menu"
            align="start"
            sideOffset={6}
            collisionPadding={12}
            onOpenAutoFocus={(e) => {
              e.preventDefault();
              list.current?.focus();
              const i = options.findIndex((o) => o.value === selected?.value);
              show(i >= 0 ? i : move(-1, 1));
            }}
          >
            <div ref={list} role="listbox" tabIndex={-1} aria-label={ariaLabel} className="bf-select-list" onKeyDown={onListKey}>
              {options.map((o, i) => (
                <Fragment key={`${o.group ?? ''}${o.value}${i}`}>
                  {o.group && o.group !== options[i - 1]?.group && <div className="bf-menu-label">{o.group}</div>}
                  <div
                    role="option"
                    data-index={i}
                    aria-selected={o.value === selected?.value}
                    aria-disabled={o.disabled || undefined}
                    data-highlighted={i === active ? '' : undefined}
                    className={cn('bf-menu-item', o.disabled && 'is-disabled')}
                    onPointerMove={() => !o.disabled && active !== i && setActive(i)}
                    onClick={() => choose(o)}
                  >
                    <span>{o.label}</span>
                    {o.value === selected?.value && <Check className="bf-menu-check" aria-hidden />}
                  </div>
                </Fragment>
              ))}
            </div>
          </RPopover.Content>
        </RPopover.Portal>
      </RPopover.Root>
    </>
  );
});
